import { AbstractControl } from '@angular/forms';

import { FieldErrors, type FieldErrorMessages } from '../constants/form-errors';

/**
 * Priority order for resolving which validation error message to display.
 * The first matching key found on the control's `.errors` object wins.
 */
const ERROR_PRIORITY: ReadonlyArray<string> = [
  'required',
  'pattern',
  'mismatch',
  'email',
  'min',
  'max',
  'minlength',
  'maxlength',
  'invalid',
];

/**
 * Resolves the human-readable error message for a form control.
 *
 * Looks up the first matching error key (by priority) from the control's
 * `.errors` against the shared `FieldErrors` map, with optional per-call
 * overrides via `customErrors`.
 *
 * @param control   - The `AbstractControl` (FormControl, FormGroup, etc.) to check.
 * @param fieldName - Key into the `FieldErrors` map (matches `formControlName`).
 * @param customErrors - Optional overrides keyed by validation error name.
 *                       These take precedence over the shared `FieldErrors` map.
 * @returns The error message string, or `null` if the control is valid.
 *
 * @example
 * ```typescript
 * const msg = getFormControlError(form.controls.email, 'email');
 * // → 'Please enter email.' (when required error is present)
 *
 * const msg = getFormControlError(form.controls.phone, 'phone', {
 *   pattern: 'Phone must be exactly 10 digits.',
 * });
 * ```
 */
export function getFormControlError(
  control: AbstractControl,
  fieldName: string,
  customErrors?: Record<string, string>,
): string | null {
  if (!control.errors) {
    return null;
  }

  const fieldMessages: FieldErrorMessages = FieldErrors[fieldName] ?? {};
  const controlErrorKeys = Object.keys(control.errors);

  for (const key of ERROR_PRIORITY) {
    if (controlErrorKeys.includes(key)) {
      // Custom overrides take precedence
      if (customErrors?.[key]) {
        return customErrors[key];
      }

      const message = fieldMessages[key as keyof FieldErrorMessages];
      if (message) {
        return message;
      }
    }
  }

  // Fallback: check any error keys not in the priority list
  for (const key of controlErrorKeys) {
    if (customErrors?.[key]) {
      return customErrors[key];
    }

    const message = fieldMessages[key as keyof FieldErrorMessages];
    if (message) {
      return message;
    }
  }

  return null;
}
